import React, {
  type FC,
  useState,
  type KeyboardEvent,
  type PropsWithChildren,
  type TouchEvent,
  useContext,
} from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import LayoutContext from '../../context/layout-context';
import Box from '../../box/box';
import QuoteCarouselArrow from './quote-carousel-arrow';
import QuoteCarouselItem from './quote-carousel-item';
import type { Lucid } from '../../../types/types';

export interface QuoteCarouselProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** List of quotes to include in the carousel */
  quotes: Lucid.Directus.Generated.Quote_Carousel_Item[];
  /** Variant for links in quote */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
  /** Typography variant for the quote text */
  quoteTextVariant?: Lucid.Theme.Component.Typography.Paragraph.VariantKeys;
}

const QuoteCarousel: FC<PropsWithChildren<QuoteCarouselProps>> = ({
  className,
  quotes,
  linkVariant,
  quoteTextVariant,
  ...rest
}) => {
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_quote_carousel_slide'],
    shortCode,
  });

  const updateCurrentIndex = (newIndex: number) => {
    if (newIndex < 0) {
      newIndex = quotes.length - 1;
    } else if (newIndex >= quotes.length) {
      newIndex = 0;
    }

    setCurrentQuoteIndex(newIndex);
  };

  const carouselTabHandler = (event: KeyboardEvent, index: number) => {
    if (event.key === 'Enter') {
      carouselArrowHandler(index);
    }
  };

  const carouselArrowHandler = (newIndex: number) => {
    updateCurrentIndex(newIndex);
  };

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 150) {
      carouselArrowHandler(currentQuoteIndex + 1);
    }

    if (touchStart - touchEnd < -150) {
      carouselArrowHandler(currentQuoteIndex - 1);
    }
  };

  return (
    <Box
      {...rest}
      css={theme => ({
        margin: 'auto',
        padding: `${theme.space.s80}px 0`,
        display: 'flex',
        justifyContent: 'center',
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <QuoteCarouselArrow
        css={theme => ({
          display: 'none',
          [theme.breakpoints.bp900.min]: {
            display: 'flex',
          },
        })}
        direction="left"
        onClick={() => carouselArrowHandler(currentQuoteIndex - 1)}
        onKeyUp={() => carouselArrowHandler(currentQuoteIndex - 1)}
      />
      <Box
        css={{
          maxWidth: '968px',
          overflow: 'hidden',
        }}
      >
        <Box
          css={{
            display: 'flex',
            transition: 'transform 1s',
            transform: `translateX(-${currentQuoteIndex * 100}%)`,
          }}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onTouchStart={handleTouchStart}
        >
          {quotes.map((quote, index) => (
            <QuoteCarouselItem
              key={`quote-item-${quote.id}`}
              isCurrentQuote={currentQuoteIndex === index}
              item={quote}
              linkVariant={linkVariant}
              quoteTextVariant={quoteTextVariant}
            />
          ))}
        </Box>
        <Box
          css={theme => ({
            display: 'flex',
            justifyContent: 'center',
            marginTop: theme.space.s40,
            height: theme.space.s40,
          })}
        >
          {quotes.map((quote, index) => {
            const isCurrentTab = index === currentQuoteIndex;

            return (
              <Box
                key={`quote-circle-${quote.id}`}
                css={theme => ({
                  height: theme.space.s40,
                  width: theme.space.s40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:focus': {
                    '& > div': {
                      height: '20px',
                      width: '20px',
                      backgroundColor: theme.colors.coolGrey[100],
                      border: `1px solid ${theme.colors.coolGrey[100]}`,
                    },
                  },
                })}
                onClick={() => carouselArrowHandler(index)}
                onKeyUp={e => carouselTabHandler(e, index)}
                tabIndex={0}
              >
                <Box
                  aria-label={withSmartling('mktg_quote_carousel_slide', {
                    index: `${index + 1}` || '',
                  })}
                  css={theme => [
                    {
                      cursor: 'pointer',
                      height: '16px',
                      width: '16px',
                      borderRadius: '50%',
                      margin: '0 8px',
                      backgroundColor: theme.colors.coolGrey[50],
                      border: `1px solid ${isCurrentTab ? theme.colors.coolGrey[100] : theme.colors.coolGrey[50]}`,
                      '&:hover': {
                        height: '20px',
                        width: '20px',
                        backgroundColor: theme.colors.coolGrey[100],
                        border: `1px solid ${theme.colors.coolGrey[100]}`,
                      },
                    },
                    isCurrentTab && {
                      height: '20px',
                      width: '20px',
                      backgroundColor: theme.colors.coolGrey[100],
                    },
                  ]}
                  role="button"
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      <QuoteCarouselArrow
        css={theme => ({
          display: 'none',
          [theme.breakpoints.bp900.min]: {
            display: 'flex',
          },
        })}
        direction="right"
        onClick={() => carouselArrowHandler(currentQuoteIndex + 1)}
        onKeyUp={() => carouselArrowHandler(currentQuoteIndex + 1)}
      />
    </Box>
  );
};

export { QuoteCarousel };
export default QuoteCarousel;
